import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { useReportingApi } from '../../../api'
import { fetchQueueStats } from '../api/queueApi'
import type { QueueStatsResponse } from '../types'
import { queueDashboardPollingOptions } from './queueDashboardPolling'

export const queueStatsQueryKeys = {
  all: ['queueStats'] as const,
  list: (pbxId: number, queueId: string | null, sampleHistoryMinutes: number) =>
    [...queueStatsQueryKeys.all, pbxId, queueId, sampleHistoryMinutes] as const,
}

export interface UseQueueStatsOptions {
  pbxId: number
  queueId: string | null
  queueNumber: string | null
  sampleHistoryMinutes: number
  refreshInterval: number
  enabled: boolean
  options?: Omit<
    UseQueryOptions<QueueStatsResponse, Error>,
    'queryKey' | 'queryFn' | 'refetchInterval' | 'staleTime' | 'enabled'
  >
}

export const useQueueStats = ({
  pbxId,
  queueId,
  queueNumber,
  sampleHistoryMinutes,
  refreshInterval,
  enabled,
  options = {},
}: UseQueueStatsOptions) => {
  const api = useReportingApi()

  return useQuery<QueueStatsResponse, Error>({
    queryKey: queueStatsQueryKeys.list(pbxId, queueId, sampleHistoryMinutes),
    queryFn: async ({ signal }) => {
      if (!queueNumber) {
        throw new Error('queueNumber is required')
      }
      // Round the window to the poll cadence so overlapping widgets and rapid
      // remounts issue an identical URL within an interval — letting the client
      // dedup and the server cache. Raw ms-precision timestamps busted both on
      // every render (support 00389200).
      const bucketMs = Math.max(refreshInterval, 1000)
      const end = Math.floor(Date.now() / bucketMs) * bucketMs
      const start = end - sampleHistoryMinutes * 60 * 1000
      const params = {
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        pbxes: String(pbxId),
        queue: queueNumber,
      }
      return fetchQueueStats(api, params, signal)
    },
    enabled: !!queueId && !!queueNumber && enabled,
    ...options,
    ...queueDashboardPollingOptions(enabled, refreshInterval),
  })
}

export default useQueueStats
